import { clearAuthUserId } from '@/platform/http/clearAuthUserId'
import { clearAllFlowData } from '@/flows/engine/clearFlowData'
import { resetAnalytics } from '@/utilities/analytics/userAnalytics'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import { clearClientAnalyticsEphemeralState } from '@/utilities/client/analyticsSession'
import { removeSelectedClientId } from '@/utilities/client/selectedClientIdStorage'
import { isAuthPagePath } from '@/utilities/paths'
import { buildPatientSessionReturnSearch } from '@/utilities/auth/patientSessionReturnPath'
import { isValidPatientRedirectPath } from '@/utilities/redirectHelpers'
import { isClientPortal } from '@/utilities/stytch/config'
import {
  clearStytchCookies,
  getStytchJWT,
  getStytchSessionTokens,
} from '@/utilities/stytch/getStytchJWT'
import { isImpersonationHandoffInProgress } from '@/utilities/stytch/impersonationHandoff'
import {
  getPatientSessionLoginRedirect,
  invalidatePatientSessionData,
  markPatientSessionExpired,
} from '@/utilities/auth/patientSession'

/**
 * Builds the login URL used after session expiration.
 * `returnPath` is where the user goes after sign-in. It defaults to the current page.
 * Patient return paths must pass the existing redirect allowlist.
 * Client Portal keeps its return path even on login pages, as it did before this change.
 */
export function buildSessionExpiredLoginUrl(
  redirectTo: string,
  returnPath?: string,
): string | null {
  if (typeof window === 'undefined') return null

  const destination = new URL(returnPath ?? window.location.pathname, window.location.origin)
  const pathname = destination.pathname
  // Query strings and hashes can hold PHI, so only the kit ID on the kit ID step is kept.
  // Its query survives so the page can validate the kit again after sign-in.
  const search = returnPath ? destination.search : window.location.search
  const returnTarget = `${pathname}${buildPatientSessionReturnSearch(pathname, search)}`
  const loginUrl = new URL(redirectTo, window.location.origin)
  if (isClientPortal()) {
    if (pathname !== redirectTo) {
      loginUrl.searchParams.set('redirectTo', pathname)
    }
  } else if (isValidPatientRedirectPath(pathname)) {
    loginUrl.searchParams.set('redirectTo', returnTarget)
  }

  loginUrl.searchParams.set('reason', 'session-expired')
  return loginUrl.toString()
}

/** Clears all browser state that belongs to the expired session. */
export function clearSessionBoundBrowserState(): void {
  invalidatePatientSessionData()
  // Call this directly. The flow-data invalidator is registered only when
  // clearFlowData.ts loads, and kit registration does not load that module.
  clearAllFlowData()
  trackEvent('session:expire')
  resetAnalytics()
  clearStytchCookies()
  clearAuthUserId()
  removeSelectedClientId()
  clearClientAnalyticsEphemeralState()
}

/** Clears session-bound browser state and loads login with reason=session-expired. */
export function runSessionExpirationCleanupAndRedirect(
  redirectTo: string,
  returnPath?: string,
): void {
  clearSessionBoundBrowserState()

  if (typeof window === 'undefined') return

  const loginUrl = buildSessionExpiredLoginUrl(redirectTo, returnPath)
  if (!loginUrl) return

  // Full document load so login receives Cross-Origin-Opener-Policy
  // `same-origin-allow-popups`. A client-side router.push keeps the previous
  // page's `same-origin` policy, and the browser then reports the Google
  // popup as closed while it is still open.
  window.location.replace(loginUrl)
}

/**
 * Click guard for patient links that need a Stytch session, such as Register Kit on the home page.
 * A link can still render after the session cookie is removed, because the page state is cached.
 * When the cookie is gone, this stops the navigation and ends the session with the
 * session-expired reason, so the patient is not sent to login without a reason.
 * After sign-in, the patient returns to `destination`, the page the link opens.
 * Returns true when the navigation may continue.
 */
function patientSessionReturnPath(path = window.location.pathname): string {
  return `${path}${window.location.search}${window.location.hash}`
}

/**
 * Returns false and starts the session-expired redirect when browser session cookies are gone.
 * Use before patient mutations on cached pages (profile save, and similar).
 */
export function ensurePatientSessionBeforeMutation(returnPath?: string): boolean {
  if (isClientPortal() || getStytchSessionTokens()) return true
  establishPatientSessionExpiration(undefined, returnPath ?? patientSessionReturnPath())
  return false
}

/**
 * Returns false and starts the session-expired redirect when this page still has a signed-in
 * patient but the Stytch cookies are gone. The Stytch member can remain after the cookies
 * are deleted. A new patient with no signed-in identity can continue, because kit registration
 * is open before sign-in.
 */
export function ensureSignedInPatientStillHasSession(isSignedInPatient: boolean): boolean {
  if (isClientPortal() || !isSignedInPatient || getStytchSessionTokens()) return true
  establishPatientSessionExpiration()
  return false
}

export function guardPatientSessionLinkClick(
  event: { preventDefault(): void },
  destination: string,
): boolean {
  if (isClientPortal() || getStytchJWT()) return true
  event.preventDefault()
  establishPatientSessionExpiration(undefined, destination)
  return false
}

/**
 * Records patient session expiration, blocks further patient actions, and
 * runs cleanup plus the session-expired redirect one time.
 */
export function establishPatientSessionExpiration(redirectTo?: string, returnPath?: string): void {
  if (typeof window === 'undefined') return
  if (isClientPortal()) return
  if (isImpersonationHandoffInProgress()) return
  // Auth pages do not redirect. Marking the session here would block the next sign-in
  // on this page until a reload. Clear only flow data here: cookies are left alone so a
  // stale in-flight failure cannot remove a sign-in that just succeeded.
  if (isAuthPagePath(window.location.pathname)) {
    invalidatePatientSessionData()
    clearAllFlowData()
    return
  }
  const loginRedirect = redirectTo ?? getPatientSessionLoginRedirect()
  if (!markPatientSessionExpired()) {
    const loginUrl = buildSessionExpiredLoginUrl(loginRedirect, returnPath)
    if (loginUrl) window.location.replace(loginUrl)
    return
  }

  runSessionExpirationCleanupAndRedirect(loginRedirect, returnPath)
}
